/**
 * All teams (`/teams`), as pure answers: each Team's queue counts off the one
 * list the queues read, and the facts its row prints from the Team's lines
 * and attachments.
 *
 * The counts come from the same grouping the Team queue uses
 * (`teamQueueGroups` over `foldPipelineRows`), so a row's Need you, Working,
 * Stopped and Done always agree with what the Team's own queue shows. Done
 * here is today's: the rows the queue's "N today" counts (`isSettledToday`).
 */
import {
  DEFAULT_JOB_INSTANCE,
  type DeskAttachment,
  type Job,
  type ReviewLevel,
  type WorkItem,
} from "@yagni/shared";

import { isSettledToday, teamQueueGroups, type QueueGroup } from "../ticket/queueGroups.js";
import { foldPipelineRows } from "../work/workViews.js";
import { filterTextOf } from "./backlogFilterText.js";
import { BACKLOG_SOURCE_WORD, backlogCardTitle } from "./copy.js";

export type QueueCounts = Record<QueueGroup, number>;

export const NO_COUNTS: QueueCounts = { needs: 0, ready: 0, live: 0, stop: 0, done: 0 };

/**
 * The All teams "Need you" cell: everything on a person, so the queue's Needs
 * you and Ready to merge together. The table has no Ready column; the Team's
 * own queue splits them.
 */
export function onAPersonCount(counts: QueueCounts): number {
  return counts.needs + counts.ready;
}

/** Every Team's queue counts from one list of work items, grouped by Team. */
export function countsByTeam(
  items: readonly WorkItem[],
  teamIds: readonly string[],
  now: number,
): Map<string, QueueCounts> {
  const byTeam = new Map<string, WorkItem[]>(teamIds.map((id) => [id, []]));
  for (const item of items) {
    if (item.archivedAt || item.deskId == null) continue;
    byTeam.get(item.deskId)?.push(item);
  }
  const out = new Map<string, QueueCounts>();
  for (const [id, teamItems] of byTeam) {
    const groups = teamQueueGroups(foldPipelineRows(teamItems), now);
    const counts = {
      ...NO_COUNTS,
      ...Object.fromEntries(groups.map((group) => [group.group, group.rows.length])),
    };
    counts.done =
      groups.find((group) => group.group === "done")?.rows.filter((row) => isSettledToday(row, now))
        .length ?? 0;
    out.set(id, counts);
  }
  return out;
}

/**
 * The rail's Team rows: how many tickets each Team has in its queue's Working
 * group (the Team page's number). A Team with none is absent.
 */
export function workingByTeam(
  items: readonly WorkItem[],
  now: number = Date.now(),
): Map<string, number> {
  const teamIds = new Set<string>();
  for (const item of items) {
    if (!item.archivedAt && item.deskId != null) teamIds.add(item.deskId);
  }
  const out = new Map<string, number>();
  for (const [id, counts] of countsByTeam(items, [...teamIds], now)) {
    if (counts.live > 0) out.set(id, counts.live);
  }
  return out;
}

/** The first live line of a kind on the Team (the default instance first). */
export function lineOf(jobs: readonly Job[], kind: string): Job | null {
  const lines = jobs.filter((job) => job.kind === kind && job.active);
  return lines.find((job) => job.instance === DEFAULT_JOB_INSTANCE) ?? lines[0] ?? null;
}

export const REVIEW_LEVEL_WORD: Record<ReviewLevel, string> = {
  light: "Light",
  balanced: "Balanced",
  tough: "Tough",
};

const GATES: ReadonlyArray<[kind: string, word: string]> = [
  ["pm.propose", "Propose"],
  ["staff.plan", "Plan"],
  ["builder.build", "Build"],
];

/**
 * The gates a person still holds among Propose, Plan and Build: "Plan, Build",
 * "None, runs end to end", or "Reviews only" for a Team with none of the three.
 */
export function gatesHeldWords(jobs: readonly Job[]): string {
  const lines = GATES.map(([kind, word]) => [lineOf(jobs, kind), word] as const).filter(
    ([line]) => line != null,
  );
  if (lines.length === 0) return "Reviews only";
  const held = lines.filter(([line]) => line!.authority !== "autonomous").map(([, word]) => word);
  return held.length > 0 ? held.join(", ") : "None, runs end to end";
}

/** How hard Proctor reviews, or "Off" when nothing reviews. */
export function reviewWords(jobs: readonly Job[]): string {
  const review = lineOf(jobs, "reviewer.review_pr");
  if (!review) return "Off";
  const level =
    (review.filter as { reviewLevel?: ReviewLevel } | undefined)?.reviewLevel ?? "balanced";
  return REVIEW_LEVEL_WORD[level] ?? REVIEW_LEVEL_WORD.balanced;
}

/** The Backlog cell: the source, then the filter as written (or the backlog's name). */
export function backlogWords(
  attachments: readonly DeskAttachment[],
): { source: string; filter: string } | null {
  const backlog = attachments.find((entry) => entry.kind !== "repo");
  if (!backlog) return null;
  return {
    source: BACKLOG_SOURCE_WORD[backlog.source] ?? backlog.source,
    filter: filterTextOf(backlog) ?? backlogCardTitle(backlog),
  };
}
